package com.rosario.juridico.premium

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import com.rosario.juridico.models.UserProfile
import com.rosario.juridico.repositories.UserRepository
import kotlinx.datetime.Clock
import kotlinx.coroutines.delay
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock

/**
 * Middleware centralizado para la gestión del estado Premium en toda la aplicación.
 * Desacopla la UI de las llamadas directas a Firestore e implementa validación de expiración.
 */
object PremiumManager {

    private const val GRACE_PERIOD_MILLIS = 172_800_000L // 48 horas de gracia
    private const val SYNC_THROTTLE_MILLIS = 300_000L // 5 min
    private const val MAX_RETRIES = 3

    /**
     * Estrategia de reintentos con backoff exponencial.
     */
    private object SyncRetryPolicy {
        const val INITIAL_DELAY = 2000L
        fun getDelay(retry: Int): Long = INITIAL_DELAY * (1 shl (retry - 1))
    }

    // --- Entitlement Premium Tipado ---

    var entitlement by mutableStateOf(PremiumEntitlement.free())
        private set

    /**
     * Indica si el usuario tiene una suscripción premium activa y vigente.
     * Implementa lógica de Grace Period: si expiró pero la última sync fue exitosa y reciente,
     * permitimos acceso temporal mientras intentamos re-sincronizar.
     */
    val isPremium: Boolean get() {
        // Cortocircuito de Seguridad: Visitante anónimo o sin email nunca es Premium
        if (com.rosario.juridico.platform.FirebaseProxy.isAnonymous() || currentUserEmail.isBlank()) {
            return false
        }

        val now = Clock.System.now().toEpochMilliseconds()
        val isActive = entitlement.isActiveManual

        // --- PARCHE DE SEGURIDAD WEB (Auditoría Fase 2) ---
        // En Web, no permitimos acceso si los datos son puramente locales (cache) sin sync.
        // Esto evita manipulación de localStorage para saltar el paywall.
        val platformName = com.rosario.juridico.getPlatform().name
        if (platformName.contains("JS", ignoreCase = true) || platformName.contains("Web", ignoreCase = true)) {
            if (entitlement.consistency == EntitlementConsistencyState.LOCAL_ONLY) {
                return false
            }
        }
        
        // Un vencimiento de 0L o -1L se considera "sin vencimiento" o "permanente" 
        // si el flag isActiveManual está en true.
        val isExpired = entitlement.expiresAt > 0L && entitlement.expiresAt < now
        
        if (isActive && !isExpired) return true
        
        // Lógica de Grace Period: solo si el flag es true pero la fecha ya pasó
        if (isActive && isExpired) {
            val timeSinceSync = now - lastSyncTime
            if (timeSinceSync < GRACE_PERIOD_MILLIS && lastSyncTime > 0) {
                return true
            }
        }
        
        return false
    }

    val currentPlan: String get() = entitlement.plan
    val vencimiento: Long get() = entitlement.expiresAt

    // --- Estado de Sincronización y UX ---

    var syncStatus by mutableStateOf(SyncStatus.IDLE)
        private set

    var currentRole by mutableStateOf("user")
        private set

    var currentUserEmail by mutableStateOf("")
        private set

    var isAuthenticated by mutableStateOf(false)
        private set

    var isEmailVerified by mutableStateOf(false)
        private set

    var lastSyncTime by mutableStateOf(0L)
        private set

    // --- Helpers / Flags Simples ---

    val isUserPremium: Boolean get() = isPremium
    val isAdmin: Boolean get() = currentRole == "admin"
    val isFree: Boolean get() = !isPremium
    val isSyncing: Boolean get() = syncStatus == SyncStatus.SYNCING || syncStatus == SyncStatus.RETRYING

    /**
     * Guardia de Seguridad (Shield Anti-Sobrescritura Promo):
     * Verifica si el entitlement actual o un perfil específico corresponde a una promoción
     * o cortesía activa (cuyo vencimiento sea futuro o indefinido).
     */
    fun isPromoEntitlementActive(
        plan: String = entitlement.plan,
        source: String = entitlement.source,
        expiresAt: Long = entitlement.expiresAt,
        isActive: Boolean = entitlement.isActiveManual
    ): Boolean {
        val now = Clock.System.now().toEpochMilliseconds()
        val isPromoPlan = plan == "cortesia_juridicorosario" || 
                          plan.contains("promo", ignoreCase = true) || 
                          plan.contains("cortesia", ignoreCase = true)
        val isPromoSource = source.equals("promo", ignoreCase = true) || 
                            source.equals("cortesia", ignoreCase = true)
        val isNotExpired = expiresAt <= 0L || expiresAt > now
        
        return (isPromoPlan || isPromoSource || (isActive && isNotExpired && plan != "free")) && isNotExpired
    }

    private var userRepository: UserRepository? = null
    private var cache: PremiumCache? = null
    private var lastLoadedUid: String? = null
    private var isLoading = false
    private var retryCount = 0
    private val mutex = Mutex()
    private var syncCountInWindow = 0
    private var lastSyncWindowStart = 0L

    // --- QA Helpers (Solo para testing controlado) ---
    private var simulatedExpirationEnabled = false
    private var simulatedStaleCacheEnabled = false
    private var simulatedOfflineEnabled = false

    // --- UX Debug Visibility (Fase 4.1 QA) ---

    data class PremiumDebugInfo(
        val isPremium: Boolean,
        val plan: String,
        val consistency: EntitlementConsistencyState,
        val syncStatus: SyncStatus,
        val lastSyncTime: Long,
        val retryCount: Int,
        val isLoading: Boolean,
        val expiresAt: Long,
        val gracePeriodActive: Boolean,
        val cacheAgeSeconds: Long,
    )

    val debugInfo: PremiumDebugInfo get() {
        val now = Clock.System.now().toEpochMilliseconds()
        
        // Ajustar consistencia visual para evitar contradicciones en el debug
        val visualConsistency = if (isLoading && syncStatus != SyncStatus.IDLE) {
            // Si estamos cargando, la consistencia actual es transicional
            if (lastSyncTime > 0) EntitlementConsistencyState.STALE_CACHE 
            else EntitlementConsistencyState.LOCAL_ONLY
        } else {
            entitlement.consistency
        }

        return PremiumDebugInfo(
            isPremium = isPremium,
            plan = entitlement.plan,
            consistency = visualConsistency,
            syncStatus = syncStatus,
            lastSyncTime = lastSyncTime,
            retryCount = retryCount,
            isLoading = isLoading,
            expiresAt = entitlement.expiresAt,
            gracePeriodActive = entitlement.consistency == EntitlementConsistencyState.GRACE_PERIOD,
            cacheAgeSeconds = if (lastSyncTime > 0) ((now - lastSyncTime) / 1000).coerceAtLeast(0) else 0
        )
    }

    /**
     * Activa simulación de expiración para testing.
     */
    fun debugSimulateExpiration(enabled: Boolean) {
        simulatedExpirationEnabled = enabled
        if (enabled) checkExpiracion()
    }

    /**
     * Activa simulación de cache obsoleta para testing.
     */
    fun debugSimulateStaleCache(enabled: Boolean) {
        simulatedStaleCacheEnabled = enabled
        if (enabled) {
            entitlement = entitlement.copy(consistency = EntitlementConsistencyState.STALE_CACHE)
            PremiumTelemetry.logCacheStaleDetected(lastLoadedUid ?: "unknown", 999999L)
        }
    }

    /**
     * Activa simulación de modo offline para testing.
     */
    fun debugSimulateOffline(enabled: Boolean) {
        simulatedOfflineEnabled = enabled
    }

    /**
     * Simula un conflicto de restauración para QA.
     * En Fase 4.1 se utiliza para simular transición a Premium y verificar reactividad.
     */
    fun debugSimulateRestoreConflict() {
        entitlement = entitlement.copy(
            isActiveManual = true, 
            plan = "PREMIUM_DEBUG",
            consistency = EntitlementConsistencyState.RESTORED
        )
        PremiumTelemetry.logRaceConditionDetected("restore_conflict_simulation")
    }

    /**
     * Configuración inicial del manager.
     */
    fun initialize(repository: UserRepository, cache: PremiumCache? = null) {
        this.userRepository = repository
        this.cache = cache
        
        // Cargar datos cacheados si existen para respuesta inmediata
        loadFromCache()
    }

    /**
     * Carga el perfil completo desde el repositorio (Firestore) con política de reintentos.
     */
    suspend fun loadUserProfile(
        uid: String, 
        forceRefresh: Boolean = false, 
        isRestore: Boolean = false,
        currentRetry: Int = 0
    ) {
        if (uid.isBlank()) return
        
        // Purga de sesión para visitantes anónimos
        if (com.rosario.juridico.platform.FirebaseProxy.isAnonymous()) {
            clearSession()
            return
        }

        val now = Clock.System.now().toEpochMilliseconds()
        
        // Simulación offline para QA
        if (simulatedOfflineEnabled) {
            syncStatus = SyncStatus.OFFLINE
            return
        }

        // Loop detection (Fase 4.1 QA)
        if (now - lastSyncWindowStart > 60_000L) {
            lastSyncWindowStart = now
            syncCountInWindow = 0
        }
        syncCountInWindow++
        if (syncCountInWindow > 10) {
            PremiumTelemetry.logSyncLoopDetected(uid, syncCountInWindow)
            return
        }

        val isStale = now - lastSyncTime > SYNC_THROTTLE_MILLIS
        
        // ELIMINADO: Throttling estricto. Si es forceRefresh, SIEMPRE entramos.
        if (!forceRefresh && uid == lastLoadedUid && !isStale) {
            PremiumTelemetry.logRedundantRefresh(uid, "throttle_active")
            return
        }

        // Protección contra duplicados de restauración
        if (isRestore && syncStatus == SyncStatus.SYNCING && isLoading) {
            PremiumTelemetry.logDuplicateRestore(uid)
            return
        }

        mutex.withLock {
            if (isLoading && currentRetry == 0) {
                PremiumTelemetry.logRaceConditionDetected("loadUserProfile_concurrent_skip")
                return
            }
            isLoading = true
        }
        
        syncStatus = if (currentRetry > 0) SyncStatus.RETRYING else SyncStatus.SYNCING
        
        try {
            val profile = userRepository?.getUser(uid)
            if (profile != null) {
                val now = Clock.System.now().toEpochMilliseconds()
                
                // 🛡️ GUARDIA DE SEGURIDAD EN PREMIUM MANAGER (Shield Anti-Sobrescritura Promo):
                // Si el perfil retornado por Firestore viene como no-premium (false), pero el perfil
                // o el estado local previo acreditan una cortesía/promoción activa (vencimiento > now),
                // OMITIMOS la degradación a 'free' y preservamos el beneficio promocional.
                val isProfilePromoActive = (profile.plan == "cortesia_juridicorosario" || 
                                           profile.plan.contains("promo", ignoreCase = true) || 
                                           profile.origenPago.contains("promo", ignoreCase = true) || 
                                           profile.origenPago.contains("cortesia", ignoreCase = true)) && 
                                          (profile.vencimiento <= 0L || profile.vencimiento > now)

                val isLocalPromoActive = isPromoEntitlementActive()

                val finalProfile = if (!profile.premium && (isProfilePromoActive || isLocalPromoActive)) {
                    PremiumTelemetry.logInconsistencyDetected("promo_active_shield", "downgrade_prevented")
                    println("[PremiumManager] 🛡️ Escudo de Seguridad: Se evitó la degradación accidental de un beneficio promocional activo para $uid.")
                    profile.copy(
                        premium = true,
                        plan = if (profile.plan == "free") "cortesia_juridicorosario" else profile.plan,
                        vencimiento = if (profile.vencimiento > 0L) profile.vencimiento else entitlement.expiresAt
                    )
                } else {
                    profile
                }

                // Validación de consistencia básica
                if (entitlement.isActiveManual && !finalProfile.premium) {
                    PremiumTelemetry.logInconsistencyDetected("premium_active", "premium_inactive_server")
                }

                lastLoadedUid = uid
                lastSyncTime = now
                retryCount = 0
                
                val consistency = when {
                    isRestore -> EntitlementConsistencyState.RESTORED
                    else -> EntitlementConsistencyState.SERVER_SYNCED
                }
                
                // Actualizar estado primero para garantizar SERVER_SYNCED antes de IDLE
                updateState(finalProfile, consistency)
                syncStatus = SyncStatus.IDLE
                
                // Telemetría
                if (isRestore) PremiumTelemetry.logRestoreSuccess(uid)
                else PremiumTelemetry.logSyncSuccess(uid, profile.plan)
                
                // Diagnóstico de origen (Fase 4.1 QA)
                PremiumTelemetry.logEntitlementResolution(entitlement)

            } else {
                handleSyncError(uid, forceRefresh, isRestore, currentRetry, Exception("User profile not found"))
            }
        } catch (e: Exception) {
            // Capturar error para asegurar liberación de flags en finally
            handleSyncError(uid, forceRefresh, isRestore, currentRetry, e)
        } finally {
            // Asegurar que isLoading pase a false solo si no hay más reintentos pendientes
            mutex.withLock {
                if (retryCount == 0) {
                    isLoading = false
                    // Se mantiene el estado FAILED_TEMPORARILY o RETRYING para permitir resurrección 
                    // automática por el Connectivity Listener si la red retorna.
                }
            }
        }
    }

    private suspend fun handleSyncError(
        uid: String, 
        forceRefresh: Boolean, 
        isRestore: Boolean, 
        currentRetry: Int,
        e: Exception
    ) {
        PremiumTelemetry.logSyncFailed(uid, e.message ?: "Unknown error")
        
        if (currentRetry < MAX_RETRIES) {
            retryCount = currentRetry + 1
            syncStatus = SyncStatus.RETRYING
            PremiumTelemetry.logRetry(uid, retryCount)
            
            // Blindaje: Uso de Backoff Exponencial configurado en SyncRetryPolicy
            val delayMillis = SyncRetryPolicy.getDelay(retryCount)
            delay(delayMillis)
            loadUserProfile(uid, forceRefresh, isRestore, retryCount)
        } else {
            retryCount = 0
            syncStatus = SyncStatus.FAILED_TEMPORARILY
            
            // Marcar como stale si ya teníamos datos
            if (entitlement.consistency == EntitlementConsistencyState.LOCAL_ONLY || 
                entitlement.consistency == EntitlementConsistencyState.SERVER_SYNCED) {
                 entitlement = entitlement.copy(consistency = EntitlementConsistencyState.STALE_CACHE)
                 // Diagnóstico de caída a cache (Fase 4.1 QA)
                 PremiumTelemetry.logEntitlementResolution(entitlement)
            }
        }
    }

    /**
     * Sincronización forzada contra el servidor.
     */
    suspend fun syncWithServer() {
        val uid = lastLoadedUid ?: com.rosario.juridico.platform.FirebaseProxy.getCurrentUserId()
        uid?.let { 
            PremiumTelemetry.logForegroundRefresh(it)
            loadUserProfile(it, forceRefresh = true) 
        }
    }

    /**
     * Refresca el estado premium consultando al repositorio.
     */
    suspend fun refreshPremium(uid: String) {
        loadUserProfile(uid, forceRefresh = true)
    }

    /**
     * Establece el estado premium de forma local (ej: tras detectar suscripción en la tienda nativa).
     * Esto permite una UX inmediata mientras se sincroniza con el backend.
     */
    fun setLocalPremium(plan: String, expiresAt: Long) {
        entitlement = PremiumEntitlement(
            plan = plan,
            expiresAt = expiresAt,
            isActiveManual = true,
            consistency = EntitlementConsistencyState.LOCAL_ONLY
        )
    }

    /**
     * Inicia un proceso de restauración de derechos.
     */
    suspend fun restoreEntitlements(uid: String) {
        loadUserProfile(uid, forceRefresh = true, isRestore = true)
    }

    /**
     * Verifica la expiración del estado actual y aplica Grace Period si aplica.
     */
    fun checkExpiracion() {
        val now = Clock.System.now().toEpochMilliseconds()
        val isActive = entitlement.isActiveManual
        
        // Simulación para QA
        val expiresAt = if (simulatedExpirationEnabled) now - 1000 else entitlement.expiresAt
        val isExpired = expiresAt != -1L && expiresAt < now
        
        if (isActive && isExpired) {
            val timeSinceSync = now - lastSyncTime
            if (timeSinceSync < GRACE_PERIOD_MILLIS && lastSyncTime > 0) {
                // Estamos en periodo de gracia
                if (entitlement.consistency != EntitlementConsistencyState.GRACE_PERIOD) {
                    entitlement = entitlement.copy(consistency = EntitlementConsistencyState.GRACE_PERIOD)
                    PremiumTelemetry.logGracePeriodEntered(lastLoadedUid ?: "unknown")
                    // Diagnóstico detallado del Grace Period (Fase 4.1 QA)
                    PremiumTelemetry.logGracePeriodDiagnosis(lastSyncTime)
                }
            } else {
                // Fuera de periodo de gracia o sin sync previa
                if (entitlement.isActiveManual) {
                    entitlement = entitlement.copy(
                        isActiveManual = false,
                        consistency = EntitlementConsistencyState.EXPIRED
                    )
                    PremiumTelemetry.logPremiumExpired(lastLoadedUid ?: "unknown")
                }
            }
        }
        
        // Marcar como STALE si ha pasado mucho tiempo
        if (lastSyncTime > 0 && now - lastSyncTime > SYNC_THROTTLE_MILLIS * 2 && 
            entitlement.consistency == EntitlementConsistencyState.SERVER_SYNCED) {
            entitlement = entitlement.copy(consistency = EntitlementConsistencyState.STALE_CACHE)
            PremiumTelemetry.logCacheStaleDetected(lastLoadedUid ?: "unknown", now - lastSyncTime)
        }
    }

    /**
     * Limpia el estado y la cache. Llamar al cerrar sesión.
     */
    fun clearSession() {
        entitlement = PremiumEntitlement.free()
        syncStatus = SyncStatus.IDLE
        currentRole = "user"
        currentUserEmail = ""
        isAuthenticated = false
        isEmailVerified = false
        lastSyncTime = 0L
        lastLoadedUid = null
        retryCount = 0
        isLoading = false
        cache?.clear()
    }

    /**
     * Actualiza el estado interno y persiste en cache si está disponible.
     */
    private fun updateState(profile: UserProfile, consistency: EntitlementConsistencyState? = null) {
        entitlement = PremiumEntitlement(
            plan = profile.plan,
            expiresAt = profile.vencimiento,
            source = profile.origenPago,
            isActiveManual = profile.premium,
            purchaseId = profile.uid,
            consistency = consistency ?: EntitlementConsistencyState.SERVER_SYNCED
        )
        
        currentRole = profile.rol
        currentUserEmail = profile.email
        isAuthenticated = profile.uid.isNotBlank() && profile.email.isNotBlank()
        isEmailVerified = com.rosario.juridico.platform.FirebaseProxy.isEmailVerified()
        
        cache?.save(profile)
    }

    private fun loadFromCache() {
        if (com.rosario.juridico.platform.FirebaseProxy.isAnonymous()) {
            entitlement = PremiumEntitlement.free()
            return
        }
        cache?.load()?.let { cached ->
            entitlement = PremiumEntitlement(
                plan = cached.plan,
                expiresAt = cached.vencimiento,
                source = cached.origenPago,
                isActiveManual = cached.premium,
                consistency = EntitlementConsistencyState.LOCAL_ONLY
            )
            
            currentRole = cached.rol
            currentUserEmail = cached.email
            isAuthenticated = cached.uid.isNotBlank() && cached.email.isNotBlank()
            checkExpiracion()
        }
    }
}



/**
 * Interfaz para permitir persistencia local según la plataforma.
 */
interface PremiumCache {
    fun save(profile: UserProfile)
    fun load(): UserProfile?
    fun clear()
}
